/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ID, WithDirtyType, WithVersion } from '@gh/common-shared-data';
import { createEnum, EnumSet, Quantity, WithOptimisticLock } from '@gh/core-data';
import { FormulaDto } from '../../common/formula.dto';
import { CostCenterSupplierFinderListItemDto } from '../../cost-centers';
import { GtoUom } from '../../take-off-params/top.dto';
import { JobOrderDetailsDto, JobOrderMethod } from '../orders';
import { JeOrderForSchedule } from '../../../builder-job-admin/jobs/resources/job-estimate-details.model';

export type JeUomRefItemDto = {
  uomId: ID;
  code: string;
  usePieces: boolean;
  dimension1?: string;
  dimension2?: string;
  dimension3?: string;
};

export type JeCcRefDto = WithVersion<{
  jobCostCenterId?: ID;
  costCenterId: ID;
  name: string;
  code: number;
  cost?: number;
  memo?: string;
}>;

export type JeTopRefItemDto = {
  topId: ID;
  code: string;
  gtoUom: GtoUom;
  isActive: boolean;
};

export type JeTopDto = {
  topId: ID;
  count: number;
  isFromGto: boolean;
  value: number;
};

export type JeCuttingListItemDto = {
  sortOrder: number;
  description: string;
  quantity: number;
  pieces?: number;
  dimension1Value?: number;
  dimension2Value?: number;
  dimension3Value?: number;
};

export type JeStrDto = {
  salesTaxRateId: ID;
  ratePct: number;
};

export type JeComponentDto = {
  jobComponentId: ID;
  code: string;
  description: string;
  cost: number;
  memo: string;
  formula?: FormulaDto;
  quantity: number;
  extension: number;
  componentId: ID;
  cuttingList?: JeCuttingListItemDto[];
  variationType: JeVariationType;
  variationStatus: JeVariationStatus;

  uomId: ID;
  topIds?: ID[];
};

export type JeDetailsBase = WithVersion<WithOptimisticLock<{
  id: ID;
  jobNumber: string;
  description: string;
  siteAddress: string;
  customerName: string;
  contactId: ID;
  cost: number;
  contractAmount: number;
  isAuthorized: boolean;
  isNotProceeding: boolean;
  isFinalized: boolean;
  variationsAmount: number;
  isDpp: boolean;
  jobDocsFolderId: ID;
  jobDocsJobPlansFolderId: ID;
  allowSupplierChange: boolean;
}>>;

export type JeDetailsDto = JeDetailsBase & WithVersion<WithOptimisticLock<{
  jobTops: JeTopDto[];
  salesTaxRates: JeStrDto[];
  jobCostCenters: JeCcRefDto[];
  jobItems: JeItemDto[];
  jobComponents: JeComponentDto[];
  jobOrders: JobOrderDetailsDto[];
  jobOrdersForSchedule: JeOrderForSchedule[];
  memo: string;
  tops: JeTopRefItemDto[];
  uoms: JeUomRefItemDto[];
}>>;

export type JeCcSaveDto = WithVersion<WithDirtyType<{
  jobCostCenterId?: ID;
  costCenterId: ID;
  memo?: string;
}>>;

export type JeComponentSaveDto = WithDirtyType<{
  jobComponentId: ID;
  description: string;
  cost: number;
  memo: string;
  formula?: FormulaDto;
  quantity: number;
  componentId: ID;
  cuttingList?: JeCuttingListItemDto[];
  variationType: JeVariationType;
  variationStatus: JeVariationStatus;
}>;

export type JeItemSaveDto = WithDirtyType<{
  jobItemId: ID;
  itemId: ID;
  jobComponentId?: ID;
  description: string;
  cost: number;
  costCenterId: ID;
  orderId?: ID;
  orderNumber?: number;
  formula?: FormulaDto;
  isLocked: boolean;
  isSalesTax: boolean;
  quantity: number;
  variationType: JeVariationType;
  variationStatus: JeVariationStatus;
  memo?: string;
  cuttingList?: JeCuttingListItemDto[];
}>;

export type JeOrderSaveDto = WithDirtyType<JobOrderDetailsDto>;

export type JeTopSaveDto = {
  topId: ID;
  value: Quantity<any>;
  count: number;
  isFromGto: boolean;
};

export type JeDirtyDataDto = {
  isSave?: boolean;
  isReprice?: boolean;
  jobCostCenters: JeCcSaveDto[];
  jobComponents: JeComponentSaveDto[];
  jobItems: JeItemSaveDto[];
  jobTops: JeTopSaveDto[];
  jobOrders: JeOrderSaveDto[];
  salesTaxRates: JeStrDto[];
  memo?: string;
};

export const JeVariationType = createEnum('JE_VARIATION_TYPE', {
  None: 0,
  Quotation: 1,
  Job: 2,
});
export type JeVariationType = EnumSet<typeof JeVariationType>;

export const JeVariationStatus = createEnum('JE_VARIATION_STATUS', {
  Unconfirmed: 0,
  Confirmed: 1,
});
export type JeVariationStatus = EnumSet<typeof JeVariationStatus>;

export type JeItemDto = WithDirtyType<{
  jobItemId: ID;
  itemId: ID;
  jobComponentId?: ID;
  code: string;
  description: string;
  cost: number;
  uomId: ID;
  costCenterId: ID;
  topIds?: ID[];
  orderId?: ID;
  orderNumber?: number;
  formula?: FormulaDto;
  isLocked: boolean;
  isSalesTax: boolean;
  quantity: number;
  quantityComponent?: number;
  extension: number;
  extensionComponent: number;
  variationType: JeVariationType;
  variationStatus?: JeVariationStatus;
  memo?: string;
  cuttingList?: JeCuttingListItemDto[];
}>;

export type JeColorSelectionDto = {
  itemName: string;
  headingName: string;
  subHeadingName: string;
  itemDescription: string;
};

export type JeCcCompleteDto = WithVersion<{
  jobCostCenterId: ID;
  costCenterId: ID;
  code: number;
  name: string;
  cost: number;
  jobItems: JeItemDto[];
  jobOrders: JobOrderDetailsDto[];
  jobColorSelection: JeColorSelectionDto[];
  suppliers: CostCenterSupplierFinderListItemDto[];
  memo?: string;
}>;

export type JeComponentItemDto = WithDirtyType<{
  jobItemId: ID; // !!! no this field in backend interface
  jobComponentId: ID;
  itemId: ID;
  costCenterId: ID;
  code: string;
  name: string;
  cost: number;
  quantity: number;
  extension: number;
  uomId: ID;
  isActive: boolean;
}>;

export type JeComponentWithItemsDto = {
  jobComponentId: ID;
  componentId: ID;
  code: string;
  cost: number;
  name: string;
  extension: number;
  formula?: FormulaDto;
  isActive: boolean;
  memo: string;
  quantity: number;
  items: JeComponentItemDto[];
  uomId: ID;
  topIds?: ID[];
};

export type JeGetComponentsResponseDto = {
  components: JeComponentWithItemsDto[];
  affectedCostCenters: JeCcRefDto[];
};

export type JeGenerateItemIdsResponseDto = { jobItemIds: ID[] };

export type JeAuthorizeResponseDto = WithVersion<Record<string, never>>;

export type JeOrderGeneratedDto = WithVersion<{
  orderId: ID;
  costCenterId: ID;
  supplierId: ID;
  supplierName: string;
  method: JobOrderMethod;
}>;

export type JeCcOrderDetailsListItemDto = {
  code: string;
  cost: number;
  quantity: number;
  extension: number;
  orderCost: number;
  orderNumber: number;
};

export type JeCcOrderDetailsDto = {
  code: number;
  name: string;
  jobItemId: ID;
  orderItems: JeCcOrderDetailsListItemDto[];
};
